import React from 'react';
import PageHero from '../components/common/PageHero';
import { Section, SectionHeading } from '../components/common/Section';
import AlignmentReadout from '../components/common/AlignmentReadout';
import BookCall from '../components/common/BookCall';

const READING = [
  {
    n: '01',
    title: 'A tight cluster is alignment',
    body: 'When answers sit close together, there is genuine shared ground — something to build on and protect.',
  },
  {
    n: '02',
    title: 'A wide spread is an overdue conversation',
    body: 'When answers are far apart, the family is carrying different assumptions. This is exactly where the work creates the most value.',
  },
  {
    n: '03',
    title: 'The gaps are usually the surprise',
    body: 'Families are often most aligned where they expected friction, and most divided where they assumed agreement.',
  },
];

const OUTCOMES = [
  'A per-dimension picture of where the family stands today',
  'The spread between members — alignment against divergence — made plain',
  'The family average alongside each individual perspective',
  'A prioritised focus for the conversation that follows',
];

const WhatYouDiscoverPage = () => {
  return (
    <div className="bg-cream">
      <PageHero
        eyebrow="What You Discover"
        title="One honest picture the whole family can see"
        subtitle="Not a verdict. A starting point — where you already agree, and where you can grow closer."
      />

      {/* The readout */}
      <Section>
        <SectionHeading
          eyebrow="The deliverable"
          title="The family alignment readout"
          intro="An illustrative example using a fictional family. Yours is built entirely from your family’s own private responses."
        />
        <div className="mt-16">
          <AlignmentReadout />
        </div>
      </Section>

      {/* How to read it */}
      <Section divider className="bg-white">
        <SectionHeading
          eyebrow="How to read it"
          title="The shape of each row says what matters"
          intro="You do not interpret data. You read clusters and spreads at a glance."
        />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {READING.map((g) => (
            <div
              key={g.n}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-secondary-200"
            >
              <span className="md:col-span-2 font-serif text-2xl text-secondary-300">
                {g.n}
              </span>
              <h3 className="md:col-span-4 font-serif text-2xl font-normal text-secondary-900 leading-snug">
                {g.title}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed text-lg">
                {g.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* What you walk away with */}
      <Section divider>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-6">
              What you walk away with
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-8">
              A beginning, not a verdict
            </h2>
            <p className="text-lg text-secondary-600 leading-relaxed">
              The readout begins the conversation. It tells the family, and the
              work, exactly where to start.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="border-t border-secondary-200">
              {OUTCOMES.map((o) => (
                <p
                  key={o}
                  className="py-5 border-b border-secondary-200 text-secondary-700 text-lg"
                >
                  {o}
                </p>
              ))}
            </div>
            <div className="mt-12">
              <BookCall size="lg">Request a conversation</BookCall>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default WhatYouDiscoverPage;
